import { motion } from 'framer-motion';
import ArrowDownIcon from '~/images/icons/ArrowDown';
import { ClickAwayListener } from '../ClickAwayListener';
import { Pressable } from '../Pressable';
import { Row } from '../Row';
import { useAutocomplete } from './hooks';
import { IOptionValue, TFilterOptionCallback, TValueChangeCallback } from './interfaces';
import { OptionList } from './OptionList';

import './styles.scss';

interface IProps<T> {
  options: IOptionValue<T>[];
  placeholder?: string;
  filterOptions?: TFilterOptionCallback<T>;
  value?: T;
  onChange: TValueChangeCallback<T>;
}

export const Autocomplete = <T,>({
  options,
  filterOptions,
  onChange,
  placeholder = 'Search',
  value,
}: IProps<T>) => {
  const {
    data,
    handleCloseOptions,
    handleOpenOptions,
    isSearching,
    isShowOptions,
    onValueChanges,
    refs,
  } = useAutocomplete({
    filterOptions,
    onChange,
    options,
    value,
  });

  return (
    <ClickAwayListener className="autocomplete" onClickAway={handleCloseOptions}>
      <Row alignItems="center" className="input-field" width="auto">
        <input
          ref={refs.input}
          placeholder={placeholder}
          style={{ all: 'unset' }}
          onFocus={handleOpenOptions}
        />
        <Pressable
          onPress={handleOpenOptions}
          style={{
            height: 24,
            width: 24,
          }}
        >
          <ArrowDownIcon />
        </Pressable>
      </Row>
      <motion.div
        ref={refs.options}
        animate={isShowOptions ? 'show' : 'hide'}
        className="options"
        initial="hide"
        transition={{ duration: 0.1 }}
        variants={{
          show: { opacity: 1, display: 'flex', zIndex: 1 },
          hide: { opacity: 0, transitionEnd: { display: 'none' } },
        }}
      >
        <OptionList data={data} isSearching={isSearching} onChange={onValueChanges} />
      </motion.div>
    </ClickAwayListener>
  );
};
